/* Display-only precision for the Judge.me product Review Widget and matching PDP hero.
 * Source: Judge.me's own jdgm.data.reviewWidget[productId], supplied with its app block.
 * Deliberately scoped to opt-in sections. No review, setting or structured-data writes.
 */
(() => {
  'use strict';
  if (window.tpjJmPrecision) return;
  const bound = new WeakSet();
  const edited = new WeakMap();
  const heroEnabled = new WeakSet();

  function productData(widget) {
    const id = widget?.getAttribute('data-product-id');
    const data = window.jdgm?.data?.reviewWidget?.[id];
    const raw = String(data?.average_rating ?? '');
    return widget?.getAttribute('data-shop-reviews') === 'false'
      && id && String(data?.product_external_id) === id
      && /^\d+(?:\.\d+)?$/.test(raw) && Number(raw) >= 1 && Number(raw) <= 5
      && Number.isInteger(data?.number_of_reviews) && data.number_of_reviews > 0
      ? data : null;
  }

  function nativeText(target) {
    const previous = edited.get(target);
    return previous && target.textContent === previous.formatted
      ? previous.nativeText : target.textContent;
  }

  function write(target, original, formatted) {
    if (formatted !== original) edited.set(target, { nativeText: original, formatted });
    else edited.delete(target);
    if (target.textContent !== formatted) target.textContent = formatted;
  }

  function updateHero(scope, root) {
    const target = root.querySelector('.price-row a.stars > span:last-child');
    if (!target) return; // Native zero-review state has no rating link.
    const original = nativeText(target);
    let heroData;
    try { heroData = JSON.parse(root.querySelector('[data-tpjp-data]').textContent); }
    catch (_) { write(target, original, original); return; }
    const id = scope.getAttribute('data-tpj-jm-product-id');
    const widget = [...scope.querySelectorAll('.jdgm-review-widget')]
      .find(element => element.getAttribute('data-product-id') === id);
    const data = productData(widget);
    const label = original.match(/^(\d+(?:\.\d+)?)(\s+from\s+([\d, ]+)\s+reviews?)$/);
    const valid = data && heroData.handle === scope.getAttribute('data-tpj-jm-product-handle')
      && label && Number(label[3].replace(/[, ]/g, '')) === data.number_of_reviews
      && !target.children.length;
    write(target, original, valid ? Number(data.average_rating).toFixed(2) + label[2] : original);
  }

  function updateNativeHero(scope, root) {
    const badge = root.querySelector('.tpj-info__rating .jdgm-prev-badge');
    const target = badge?.querySelector('.jdgm-prev-badge__text');
    if (!target) return;
    const original = nativeText(target);
    const id = scope.getAttribute('data-tpj-jm-product-id');
    const widget = [...scope.querySelectorAll('.jdgm-review-widget')]
      .find(element => element.getAttribute('data-product-id') === id);
    const data = productData(widget);
    const label = original.match(/^(\s*)(?:\d+(?:\.\d+)?\s+from\s+)?([\d, ]+\s+reviews?)(\s*)$/);
    const count = label ? Number(label[2].match(/^[\d, ]+/)[0].replace(/[, ]/g, '')) : NaN;
    const valid = data && label && !target.children.length
      && data.number_of_reviews === count
      && Number(badge.getAttribute('data-number-of-reviews')) === count
      && badge.hasAttribute('data-average-rating')
      && Number(badge.getAttribute('data-average-rating')) === Number(data.average_rating);
    write(target, original, valid
      ? label[1] + Number(data.average_rating).toFixed(2) + ' from ' + label[2] + label[3]
      : original);
  }

  function update(scope) {
    scope.querySelectorAll('.jdgm-review-widget').forEach(widget => {
      const display = widget.querySelector('.jm-review-widget__header .jm-average-rating-display');
      if (!display) return;
      const labels = display.querySelectorAll('p.jm-text');
      if (labels.length !== 2) return;
      const target = labels[0];
      const original = nativeText(target);
      const data = productData(widget);
      const countLabel = labels[1].textContent.trim().match(/^([\d, ]+) reviews?$/);
      const visibleCount = countLabel ? Number(countLabel[1].replace(/[, ]/g, '')) : NaN;
      const valid = data && data.number_of_reviews === visibleCount
        && /^\d+(?:\.\d+)?$/.test(original.trim())
        && !target.children.length;
      write(target, original, valid ? Number(data.average_rating).toFixed(2) : original);
    });
    const id = scope.getAttribute('data-tpj-jm-product-id');
    if (id && [...scope.querySelectorAll('.jdgm-review-widget')]
      .some(widget => widget.getAttribute('data-product-id') === id)) heroEnabled.add(scope);
    if (heroEnabled.has(scope)) {
      document.querySelectorAll('[data-tpjp-main]').forEach(root => {
        observe(root, () => update(scope));
        updateHero(scope, root);
      });
      document.querySelectorAll('.tpj-pdp').forEach(root => {
        observe(root, () => update(scope));
        updateNativeHero(scope, root);
      });
    }
  }

  function observe(element, callback) {
    if (bound.has(element)) return;
    bound.add(element);
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => { queued = false; callback(); });
    }).observe(element, {
      subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['data-product-id', 'data-shop-reviews', 'data-tpj-jm-product-id', 'data-tpj-jm-product-handle', 'data-average-rating', 'data-number-of-reviews']
    });
  }

  function bind() {
    document.querySelectorAll('[data-tpj-jm-precision]').forEach(scope => {
      // Judge.me mounts asynchronously and replaces header nodes on some updates.
      // Coalesce mutations; writing only changed text makes our own update idempotent.
      observe(scope, () => update(scope));
      update(scope);
    });
  }
  window.tpjJmPrecision = { bind };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
  else bind();
  document.addEventListener('shopify:section:load', bind);
})();
